import Layout from "../components/Layout";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { FiCheckCircle, FiTruck, FiSettings, FiShield } from "react-icons/fi";
import { FaQuoteLeft, FaStar } from "react-icons/fa";
import Slider from "react-slick";
import { FiDroplet, FiTool, FiSun, FiBox, FiZap, FiPlus } from "react-icons/fi";
import { Link } from "react-router-dom";

const products = [
  {
    title: "High-Performance Pump",
    description: "Energy-efficient circulation for all pool sizes.",
    image: "https://www.astralpool.com/content/dam/astralpool/images/category/pumps/cat2_variable_speed_pumps.png/jcr:content/renditions/cq5dam.web.1280.1280.png"
  },
  {
    title: "Sand Filter System",
    description: "Advanced filtration with low maintenance.",
    image: "https://asset.productmarketingcloud.com/api/assetstorage/3096_dea61585-6320-487a-b428-3048d9b059c4/ProductPage"
  },
  {
    title: "LED Pool Lights",
    description: "Multicolor lighting for elegant ambiance.",
    image: "https://asset.productmarketingcloud.com/api/assetstorage/3096_1d777d4c-f0d0-4abf-be34-35a4ba5fa0ea/ProductPage"
  },
  {
    title: "Dosing Unit",
    description: "Smart control of pool water chemistry.",
    image: "https://www.astralpool.com/content/dam/astralpool/images/category/water-treatment-equipment/cat3_Dosing_systems_Desktop_569x401px.png/jcr:content/renditions/cq5dam.web.1280.1280.png"
  },
  {
    title: "Pool Cleaner Robot",
    description: "Auto-cleaning for pristine pool surfaces.",
    image: "https://www.astralpool.com/content/dam/astralpool/images/category/cleaning/cat3_Plastic%20brushes_Desktop_569x401px.png/jcr:content/renditions/cq5dam.web.1280.1280.png"
  },
  {
    title: "Electric Heater",
    description: "Fast water heating with safety features.",
    image: "https://www.astralpool.com/content/dam/astralpool/images/category/dehumidification/cat2_Large%20pool%20range.jpg/jcr:content/renditions/cq5dam.web.1280.1280.jpeg"
  },
  {
    title: "Skimmer Kit",
    description: "Essential for surface debris removal.",
    image: "https://www.astralpool.com/content/dam/astralpool/images/category/pool-shell-equipment/cat3_ABS%20Skimmer_Desktop_569x401px.png/jcr:content/renditions/cq5dam.web.1280.1280.png"
  },
  {
    title: "Stainless Ladder",
    description: "Safe and durable access for all pools.",
    image: "https://asset.productmarketingcloud.com/api/assetstorage/3096_6d3c551a-2d44-4908-b7f7-897a2cabde36/ProductPage"
  }
];

const testimonials = [
  {
    name: "Nadia Ben Salah",
    role: "Private Villa Owner, Nabeul",
    quote: "The LED lights and filter system from TriTech exceeded expectations. Quick delivery and reliable quality!",
    avatar: "/assets/images/emily.png",
    rating: 5
  },
  {
    name: "Slim Taktak",
    role: "Hotel Pool Manager, Sousse",
    quote: "We've upgraded three pools with TriTech's heating systems. Efficiency and support were top-notch.",
    avatar: "/assets/images/mohamed.png",
    rating: 4
  }
];


const productCategories = [
    {
      icon: <FiDroplet size={24} />,
      title: "Filtration Systems",
      description: "High-efficiency filters and pumps for clean, safe pool water.",
      link: "/products/filtration"
    },
    {
      icon: <FiSun size={24} />,
      title: "Heating & Lighting",
      description: "LEDs, solar heating, and automation for 24/7 comfort.",
      link: "/products/heating-lighting"
    },
    {
      icon: <FiTool size={24} />,
      title: "Automatic Cleaners",
      description: "Robotic and pressure cleaners for effortless pool upkeep.",
      link: "/products/cleaners"
    },
    {
      icon: <FiBox size={24} />,
      title: "Accessories",
      description: "Skimmers, ladders, covers, and luxury additions.",
      link: "/products/accessories"
    },
    {
      icon: <FiZap size={24} />,
      title: "Water Treatment",
      description: "Dosing systems, chlorine, saltwater kits, and test kits.",
      link: "/products/treatment"
    },
    {
      icon: <FiSettings size={24} />,
      title: "Control & Automation",
      description: "Smart controllers, timers, and remote pool management systems.",
      link: "/products/automation"
    }
  ];
  

const Products = () => {
  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    arrows: false,
    slidesToShow: 1.1,
    slidesToScroll: 1,
    responsive: [{ breakpoint: 768, settings: { slidesToShow: 1.2 } }]
  };

  return (
    <Layout>
      <Helmet>
        <title>Pool Equipment & Accessories | TriTech Tunisie</title>
        <meta
          name="description"
          content="Explore premium pool pumps, filters, heating, lighting and more. Delivered across Tunisia by TriTech."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-blue-800 text-white py-28 px-6 text-center">
        <motion.h1
          className="text-4xl font-extrabold mb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Explore Our Pool Equipment Range
        </motion.h1>
        <motion.p
          className="max-w-2xl mx-auto text-lg"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          TriTech Tunisie offers premium products for every pool type—engineered for performance, safety, and aesthetics.
        </motion.p>
      </section>


        {/* Product Categories */}
        <section className="py-20 px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {productCategories.map((cat, index) => (
            <motion.div
              key={index}
              className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition border text-left"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="text-primary mb-4">{cat.icon}</div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">{cat.title}</h3>
              <p className="text-sm text-gray-600 mb-4">{cat.description}</p>
              <Link
                to={cat.link}
                className="inline-flex items-center text-sm text-primary font-medium hover:underline"
              >
                Learn More <FiPlus className="ml-2" />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

   {/* Product Grid */}
<section className="py-10 px-6">
  <div className="max-w-7xl mx-auto text-center mb-10">
    <h2 className="text-3xl font-bold text-gray-900">Featured Pool Products</h2>
    <p className="text-gray-600 mt-2">Explore our most requested filtration, heating, and lighting systems</p>
  </div>

  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
    {products.map((p, i) => (
      <div key={i} className="bg-white shadow rounded-xl overflow-hidden hover:shadow-md transition">
        <img src={p.image} alt={p.title} className="w-full h-48 object-cover" />
        <div className="p-5 text-left">
          <h3 className="font-semibold text-lg text-gray-800 mb-2">{p.title}</h3>
          <p className="text-sm text-gray-600">{p.description}</p>
        </div>
      </div>
    ))}
  </div>

  <div className="text-center mt-12">
    <a
      href="/products/all"
      className="inline-block w-full sm:w-auto bg-primary text-white font-semibold py-3 px-8 rounded-full hover:bg-primary/90 transition"
    >
      Browse All Products
    </a>
  </div>
</section>


      {/* Why Choose Section */}
      <section className="bg-white py-20 px-6 border-t">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Choose Our Products</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-10">
            {[{
              icon: <FiShield size={24} />, title: "Certified European Standards"
            }, {
              icon: <FiCheckCircle size={24} />, title: "Durable & Long-lasting"
            }, {
              icon: <FiTruck size={24} />, title: "Delivered Across Tunisia"
            }, {
              icon: <FiSettings size={24} />, title: "Full Installation Support"
            }].map((item, idx) => (
              <div key={idx} className="flex flex-col items-center">
                <div className="text-primary mb-2">{item.icon}</div>
                <p className="text-sm text-gray-700 font-semibold text-center">{item.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-gray-100 py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h2
            className="text-3xl font-bold text-gray-900 mb-12"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            What Our Clients Say
          </motion.h2>

          <div className="md:hidden">
            <Slider {...sliderSettings}>
              {testimonials.map((t, i) => (
                <div key={i} className="pr-4">
                  <TestimonialCard testimonial={t} />
                </div>
              ))}
            </Slider>
          </div>

          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((t, i) => (
              <TestimonialCard key={i} testimonial={t} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Request Quote */}
      <section className="bg-primary text-white py-20 px-6 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold mb-4">Need help choosing the right filter or heater?</h2>
          <p className="text-sm mb-8">
            Let our specialists assist you in selecting the best equipment for your pool. We offer consultations and installation support.
          </p>
          <a
            href="/quote-request"
            className="inline-block bg-white text-primary px-6 py-3 rounded-full font-medium hover:bg-gray-100"
          >
            Request a Quote
          </a>
          <div className="mt-4 text-sm">
            Or message us on <a href="https://wa.me/21612345678" className="underline">WhatsApp</a> or call <a href="tel:+21612345678" className="underline">+216 55 050 050</a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

const TestimonialCard = ({ testimonial }) => (
  <motion.div
    className="bg-white rounded-xl shadow-lg p-6 text-left hover:shadow-xl transition-all duration-300 relative flex flex-col h-full"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    viewport={{ once: true }}
  >
    <div className="flex items-center mb-4 gap-3">
      <img
        src={testimonial.avatar}
        alt={testimonial.name}
        className="w-12 h-12 rounded-full object-cover border border-gray-200"
      />
      <div>
        <h4 className="font-semibold text-gray-900 text-sm">{testimonial.name}</h4>
        <span className="text-xs text-gray-500">{testimonial.role}</span>
      </div>
    </div>
    <FaQuoteLeft className="text-primary mb-3" size={20} />
    <p className="text-gray-700 text-base leading-relaxed italic mb-4 flex-1">
      {testimonial.quote}
    </p>
    <div className="flex space-x-1 mt-auto">
      {Array.from({ length: testimonial.rating }).map((_, idx) => (
        <FaStar key={idx} className="text-yellow-400" size={14} />
      ))}
    </div>
  </motion.div>
);

export default Products;
